import React, { useCallback } from 'react';
import Grid from '@material-ui/core/Grid';

import { Iso6391Locale } from 'yeself-common/typings/model/Model';
import { ElementLocation, ElementLocationPath } from 'yeself-common/typings/guide/LayerCommon';
import {
    ELEMENT_PATHS,
    ELEMENT_PATH_ORDER,
    ELEMENT_PATH_SEARCH_STRATEGY,
    NO_PATH,
} from 'yeself-common/enum/ModelEnum';

import PathField from 'yeself-client-common/form/components/PathField';
import PathPreviewButton from 'yeself-client-common/form/components/PathPreviewButton';

import {
    getAllElementsCountByCssPath,
    getAllElementsCountByXpath,
    getAllElementsCountForJsPath,
} from 'yeself-client-common/utils/DomUtils';

type PathPreviewButtonProps = {
    onClick: (path: ElementLocationPath) => void;
};

type LocationPathsFormProps = {
    onChange: (value: ElementLocation) => void;
    value: ElementLocation;
    locale: Iso6391Locale;
    showMatches?: boolean;
    pathPreviewButton?: PathPreviewButtonProps;
};

const defaultElementLocationPath = (
    type: typeof ELEMENT_PATHS[keyof typeof ELEMENT_PATHS],
    locale: Iso6391Locale
): ElementLocationPath => {
    switch (type) {
        case ELEMENT_PATHS.XPATH:
            return {
                id: type,
                enabled: false,
                path: { [locale]: NO_PATH },
                searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
            };
        case ELEMENT_PATHS.JS_PATH:
            return {
                id: type,
                enabled: false,
                path: NO_PATH,
                searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.JS_PATH_QUERY,
            };
        case ELEMENT_PATHS.JS_PATH_NON_RELATIVE:
            return {
                id: type,
                enabled: false,
                path: NO_PATH,
                searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.JS_PATH_QUERY,
            };
        default:
            return {
                id: type,
                enabled: false,
                path: NO_PATH,
                searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
            };
    }
};

const calculateMatches = (path: ElementLocationPath, locale: Iso6391Locale): number => {
    switch (path.id) {
        case ELEMENT_PATHS.XPATH:
            return locale && path.path && path.path[locale] && path.path[locale] !== NO_PATH
                ? getAllElementsCountByXpath(path.path[locale])
                : 0;
        case ELEMENT_PATHS.XPATH_LIMITED:
            return path.path && path.path !== NO_PATH ? getAllElementsCountByXpath(path.path) : 0;
        case ELEMENT_PATHS.JS_PATH:
        case ELEMENT_PATHS.JS_PATH_NON_RELATIVE:
            return path.path && path.path !== NO_PATH ? getAllElementsCountForJsPath(path.path) : 0;
        default:
            return path.path && path.path !== NO_PATH ? getAllElementsCountByCssPath(path.path) : 0;
    }
};

const locationPathByLocale = (path: ElementLocationPath, locale: Iso6391Locale): ElementLocationPath => {
    switch (path.id) {
        case ELEMENT_PATHS.XPATH:
            return {
                ...path,
                path: path.path[locale],
            } as unknown as ElementLocationPath;
        default:
            return path;
    }
};

const pathNotEmpty = (path: ElementLocationPath, locale: Iso6391Locale): boolean => {
    switch (path.id) {
        case ELEMENT_PATHS.XPATH:
            return (
                path &&
                path.path !== undefined &&
                path.path[locale] !== undefined &&
                path.path[locale] !== NO_PATH &&
                path.path[locale].length > 0
            );
        default:
            return path.path !== undefined && path.path !== NO_PATH && path.path.length > 0;
    }
};

const LocationPathsForm = ({
    onChange,
    value,
    locale,
    showMatches = false,
    pathPreviewButton,
}: LocationPathsFormProps): React.ReactElement => {
    const handleOnPathChange = useCallback(
        (newValue: ElementLocationPath) => {
            onChange({
                ...value,
                paths: ELEMENT_PATH_ORDER.map((type) => {
                    const existingPath =
                        value.paths.find((path) => path.id === type) ||
                        defaultElementLocationPath(type, locale);

                    return existingPath.id === newValue.id ? newValue : existingPath;
                }),
            });
        },
        [value, onChange, locale]
    );

    return (
        <Grid container spacing={16}>
            {ELEMENT_PATH_ORDER.map((type) => {
                const path = value.paths.find((path) => path.id === type);
                const matches = path ? calculateMatches(path, locale) : 0;

                return (
                    <Grid
                        key={type}
                        item
                        xs={12}
                        style={{ backgroundColor: '#f8f8f8', marginBottom: 8, borderRadius: 4 }}
                    >
                        <PathField
                            onChange={handleOnPathChange}
                            value={path || defaultElementLocationPath(type, locale)}
                            locale={locale}
                            {...(showMatches && path && pathNotEmpty(path, locale) ? { matches } : {})}
                        />
                        {pathPreviewButton && path && path.enabled && matches > 0 && (
                            <Grid item container direction="row-reverse">
                                <PathPreviewButton
                                    onClick={() =>
                                        pathPreviewButton?.onClick(locationPathByLocale(path, locale))
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default LocationPathsForm;
